import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BiArrowBack, BiDownload, BiSpreadsheet } from "react-icons/bi";
import moment from "moment/moment";

import {
  getMJS,
  getMjsV2,
  getQuatationOperasional,
  quatationOperasionalSelector,
} from "./quatationOperasionalSlice";
import { getMinKlien, klienSelector } from "../../../features/master/klien/klienSlice";
import { Colors } from "../../../assets/Colors";
import HandlingFeeBasePrice from "./HandlingFeeBasePrice";
import "./quatation.css";

const API = process.env.REACT_APP_API_URL;

// 4 angka di belakang koma, tanpa pembulatan ke bilangan bulat.
const nf = (n) =>
  new Intl.NumberFormat("id-ID", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(Number(n) || 0);
const rp = (n) => "Rp " + nf(n);

// Label group (LineGroup) untuk kolom Sub Activity.
const GROUP_LABELS = { SERVICE: "Service", EQUIPMENT: "Equipment", LABOUR: "Labour", TRUCK: "Truck" };

// Kolom Sub Activity / Item Activity berbeda per jenis aktivitas (sesuai data lama).
function subItemCols(activityName, sub) {
  if (activityName === "RENTAL") return { sub: "RENTAL", item: sub.Surname || "" };
  if (activityName === "MANPOWER" || activityName === "PORT_SERVICES")
    // Sub Activity = group (Service/Equipment/Labour/Truck); Item Activity = nama item.
    return { sub: GROUP_LABELS[sub.LineGroup] || sub.LineGroup || "Service", item: sub.Surname || "" };
  return { sub: (sub.Name || "").replace(/_/g, " "), item: sub.Surname || "" };
}

// Bangun model baris terstruktur dari mjsData, dikelompokkan per Item.
// Item yang sama dari beberapa JR digabung jadi satu baris (pemakaian harian dijumlahkan).
// usageOnly: rate diabaikan saat penggabungan (rate tak ditampilkan, jadi item sama
// dengan rate berbeda tampil satu baris).
function buildRows(mjsData, dayCount, usageOnly = false) {
  const activities = (mjsData && mjsData.Activity) || [];
  const rows = [];
  let no = 1;
  let grandTotal = 0;

  activities.forEach((act) => {
    const name = (act.NamaActivity || "").replace(/_/g, " ");
    const detail = act.Detail || [];

    // Gabungkan baris item yang sama (Sub Activity + Item + UOM + Rate).
    const groups = new Map();
    detail.forEach((sub) => {
      const rate = parseFloat(sub.Rate) || 0;
      const { sub: subCol, item: itemCol } = subItemCols(act.NamaActivity, sub);
      const uom = sub.SatuanID || "";
      const key = [subCol, itemCol, uom, usageOnly ? "" : rate].join("|");
      let g = groups.get(key);
      if (!g) {
        g = { sub: subCol, item: itemCol, uom, rate, days: Array(dayCount).fill(0), minAdj: 0 };
        groups.set(key, g);
      }
      (sub.Transaksi || []).forEach((t, i) => {
        if (i < dayCount) g.days[i] += parseFloat(t.Quatity) || 0;
      });
      // Selisih ke qty tagih JR (MinUsage) — tak punya tanggal, masuk Total Usage saja.
      g.minAdj += parseFloat(sub.MinAdj) || 0;
    });

    let actTotal = 0;
    groups.forEach((g) => {
      const totalUsage = g.days.reduce((a, b) => a + b, 0) + g.minAdj;
      const totalCost = totalUsage * g.rate;
      const isFsc = act.NamaActivity === "Tagihan_Fuel_Surcharge";
      actTotal += totalCost;
      rows.push({
        type: "item",
        no: no++,
        activity: name,
        sub: g.sub,
        item: g.item,
        uom: g.uom,
        rate: g.rate,
        totalUsage,
        totalCost,
        minAdj: g.minAdj,
        days: g.days,
        fsc: isFsc,
      });
    });

    if (groups.size) {
      rows.push({ type: "subtotal", label: `Sub Total ${name}`, total: actTotal });
      grandTotal += actTotal;
    }
  });

  return { rows, grandTotal };
}

export default function MJSPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const param = useLocation();
  const quatation = useSelector(quatationOperasionalSelector);
  const klien = useSelector(klienSelector);

  const quotationNo = param.state?.QuatationNo ?? null;
  const isV2 = !!param.state?.isV2;
  const headerState = param.state?.header ?? {};
  // usageOnly: dibuka dari daftar Job Request — hanya pemakaian (tanpa Rate/Total Cost).
  const usageOnly = !!param.state?.usageOnly;
  const backTo = param.state?.backTo || "/quotation-or-contract-list";
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [month, setMonth] = useState(() => {
    const m = param.state?.month ? moment(param.state.month, "YYYY-MM", true) : null;
    return m && m.isValid() ? m.toDate() : new Date();
  });
  const [requested, setRequested] = useState(false); // sudah klik "Buka"
  const [downloading, setDownloading] = useState(false);

  const dayCount = moment(month).daysInMonth();
  // Header: V2 dikirim dari daftar QO; legacy diambil dari getQuatation.
  const header = isV2 ? headerState : (quatation.data || {});

  useEffect(() => {
    if (!quotationNo) return;
    dispatch(getMinKlien({ search: "" }));
    if (!isV2) dispatch(getQuatationOperasional({ QuatationNo: quotationNo }));
  }, [dispatch, quotationNo, isV2]);

  // Model baris dihitung dari data (tanpa efek samping yang berjalan tiap render).
  const { rows, grandTotal } = useMemo(
    () => buildRows(quatation.mjsData, dayCount, usageOnly),
    [quatation.mjsData, dayCount, usageOnly]
  );

  const loadingMjs = requested && !quatation.isGetDetailMJS;

  // Hari bertagihan FSC yang belum punya Base Price BBM Solar (dihitung server).
  const fscMissing = useMemo(
    () => ((quatation.mjsData && quatation.mjsData.Activity) || [])
      .flatMap((a) => a.Detail || [])
      .reduce((n, d) => n + (d.FscMissing || 0), 0),
    [quatation.mjsData]
  );

  // Entri Handling Fee yang belum punya Base Price (dihitung server).
  const hfMissing = useMemo(
    () => ((quatation.mjsData && quatation.mjsData.Activity) || [])
      .flatMap((a) => a.Detail || [])
      .reduce((n, d) => n + (d.HfMissing || 0), 0),
    [quatation.mjsData]
  );

  const handleGenerate = () => {
    const start = moment(month).startOf("month").toISOString();
    const end = moment(month).endOf("month").toISOString();
    setRequested(true);
    const args = { tanggal1: start, tanggal2: end, QuatationNo: quotationNo };
    dispatch(isV2 ? getMjsV2(args) : getMJS(args));
  };

  const handleDownloadExcel = async () => {
    const url = ("MJS-" + quotationNo + ".xlsx").replaceAll("/", "-");
    setDownloading(true);
    try {
      const res = await fetch(API + "excel/mjs?id=" + url, {
        method: "GET",
        headers: { Accept: "*/*", Authorization: "Bearer " + user.Token },
      });
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", url);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      // diam-diam saja; tombol kembali aktif
    } finally {
      setDownloading(false);
    }
  };

  if (!quotationNo) {
    return (
      <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
        <div className="text-center">
          <p className="mb-2">Quotation tidak dipilih</p>
          <button onClick={() => navigate(backTo)} className="text-orange-500 underline text-sm">
            Kembali
          </button>
        </div>
      </div>
    );
  }

  // No, Activity, Sub, Item, UOM, Rate, Total Usage, Total Cost (usageOnly: tanpa Rate & Total Cost)
  const fixedCols = usageOnly ? 6 : 8;
  const klienName = isV2
    ? (header.NamaKlien || header.KlienID || "—")
    : ((klien.minData || []).find((o) => o.value === header.KlienID)?.label || header.KlienID || "—");
  const expireDisplay = isV2
    ? (header.ExpireDate || "—")
    : (header.ExpireDate ? moment(header.ExpireDate).format("DD-MM-YYYY") : "—");

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      {/* Top bar */}
      <div className="flex items-center justify-between py-4">
        <button
          onClick={() => navigate(backTo)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <span className="text-sm font-semibold" style={{ color: Colors.primary }}>
          Monthly Job Summary (MJS){usageOnly ? " — Usage" : ""}
        </span>
      </div>

      {/* Info Quotation */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Info label="No. Quotation" value={quotationNo} mono />
          <Info label="No. Kontrak" value={header.NoKontrak} />
          <Info label="Klien" value={klienName} />
          <Info label="Expire Date" value={expireDisplay} />
          <div className="md:col-span-4">
            <Info label="Work Description" value={header.WorkDesc} />
          </div>
        </div>
      </div>

      {/* Kontrol: pilih bulan + aksi */}
      <div className="bg-white rounded-xl border p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Bulan</label>
            <DatePicker
              dateFormat="MMMM yyyy"
              selected={month}
              onChange={(d) => setMonth(d)}
              showMonthYearPicker
              className="w-44 border rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400"
            />
          </div>
          <button
            onClick={handleGenerate}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
            style={{ background: Colors.primary }}
          >
            <BiSpreadsheet /> Buka MJS
          </button>
          {/* Excel memuat Rate & Total Cost → tidak ditawarkan di mode usage. */}
          {!usageOnly && requested && quatation.isGetDetailMJS && rows.length > 0 && (
            <button
              onClick={handleDownloadExcel}
              disabled={downloading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium disabled:opacity-50"
              style={{ borderColor: Colors.primary, color: Colors.primary }}
            >
              <BiDownload /> {downloading ? "Mengunduh…" : "Download Excel"}
            </button>
          )}
        </div>
      </div>

      {/* Base Price Handling Fee — diisi sebelum Rekap Pemakaian & invoicing (mode usage: tak ditampilkan) */}
      {!usageOnly && (
        <HandlingFeeBasePrice quotationNo={quotationNo} onChanged={() => requested && handleGenerate()} />
      )}

      {/* Peringatan Handling Fee tanpa Base Price */}
      {requested && quatation.isGetDetailMJS && hfMissing > 0 && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <strong>{hfMissing} entri Handling Fee belum punya Base Price</strong> (tanggalnya di luar rentang harga). Biayanya
          belum dihitung — lengkapi Start/End Date Price di atas.
        </div>
      )}

      {requested && quatation.isGetDetailMJS && fscMissing > 0 && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <strong>Tagihan Fuel Surcharge belum lengkap:</strong> {fscMissing} hari ber-activity FSC belum punya Base Price
          <strong> BBM Solar</strong>. Isi Start/End Date Price item BBM Solar di card Base Price di atas.
        </div>
      )}

      {/* Tabel MJS */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b font-semibold text-sm flex items-center justify-between"
          style={{ background: Colors.primary + "15", color: Colors.primary }}>
          <span>Rekap Pemakaian — {moment(month).format("MMMM YYYY")}</span>
          {!usageOnly && requested && quatation.isGetDetailMJS && (
            <span className="text-xs font-normal text-gray-600">Grand Total: <strong>{rp(grandTotal)}</strong></span>
          )}
        </div>

        {!requested ? (
          <div className="py-14 text-center text-gray-400 text-sm">
            Pilih bulan lalu klik <strong>Buka MJS</strong> untuk menampilkan rekap.
          </div>
        ) : loadingMjs ? (
          <div className="py-14 text-center text-gray-400 text-sm">Memuat data MJS…</div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center text-gray-400 text-sm">
            Tidak ada data transaksi untuk {moment(month).format("MMMM YYYY")}.
          </div>
        ) : (
          <div className="overflow-auto max-h-[70vh]">
            <table className="text-xs border-collapse w-max min-w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-100 text-gray-600">
                  <Th className="text-center w-10">No</Th>
                  <Th>Activity</Th>
                  <Th>Sub Activity</Th>
                  <Th>Item Activity</Th>
                  <Th className="text-center">UOM</Th>
                  {!usageOnly && <Th className="text-right">Rate (IDR)</Th>}
                  <Th className="text-right">Total Usage</Th>
                  {!usageOnly && <Th className="text-right">Total Cost</Th>}
                  {Array.from({ length: dayCount }, (_, i) => (
                    <Th key={i} className="text-center w-9">{i + 1}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) =>
                  r.type === "subtotal" ? (usageOnly ? null : (
                    <tr key={idx} className="bg-blue-50 font-semibold text-blue-800">
                      <td className="px-2 py-1.5 text-right border border-gray-300" colSpan={fixedCols - 1}>
                        {r.label}
                      </td>
                      <td className="px-2 py-1.5 text-right border border-gray-300">{rp(r.total)}</td>
                      <td className="border border-gray-300" colSpan={dayCount} />
                    </tr>
                  )) : (
                    <tr key={idx} className="hover:bg-orange-50 even:bg-gray-50/40">
                      <td className="px-2 py-1.5 text-center border border-gray-300">{r.no}</td>
                      <td className="px-2 py-1.5 border border-gray-300 whitespace-nowrap">{r.activity}</td>
                      <td className="px-2 py-1.5 border border-gray-300 whitespace-nowrap">{r.sub || "—"}</td>
                      <td className="px-2 py-1.5 border border-gray-300 whitespace-nowrap">{r.item || "—"}</td>
                      <td className="px-2 py-1.5 text-center border border-gray-300">{r.uom || "—"}</td>
                      {!usageOnly && (
                        <td className="px-2 py-1.5 text-right border border-gray-300 whitespace-nowrap">{r.fsc ? "—" : nf(r.rate)}</td>
                      )}
                      <td className="px-2 py-1.5 text-right border border-gray-300 font-medium whitespace-nowrap"
                        title={r.minAdj > 0 ? `Pemakaian ${nf(r.totalUsage - r.minAdj)}, ditagih minimum ${nf(r.totalUsage)} (Min. Usage)` : undefined}>
                        {r.fsc ? "—" : nf(r.totalUsage)}
                        {!r.fsc && r.minAdj > 0 && <span className="ml-1 text-[10px] font-semibold text-amber-600">min</span>}
                      </td>
                      {!usageOnly && (
                        <td className="px-2 py-1.5 text-right border border-gray-300 font-medium whitespace-nowrap">{nf(r.totalCost)}</td>
                      )}
                      {r.days.map((v, di) => (
                        <td key={di} className="px-1.5 py-1.5 text-right border border-gray-300 text-gray-600 whitespace-nowrap">
                          {/* Kolom tanggal selalu qty pemakaian per hari (bukan nominal);
                              nominal cukup di Rate & Total Cost. */}
                          {v ? nf(v) : ""}
                        </td>
                      ))}
                    </tr>
                  )
                )}
                {/* Grand total */}
                {!usageOnly && (
                  <tr className="bg-blue-100 font-bold text-blue-900">
                    <td className="px-2 py-2 text-right border border-gray-300" colSpan={fixedCols - 1}>GRAND TOTAL</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap border border-gray-300">{rp(grandTotal)}</td>
                    <td className="border border-gray-300" colSpan={dayCount} />
                  </tr>
                )}
              </tbody>
            </table>
            {rows.some((r) => r.minAdj > 0) && (
              <p className="px-4 py-2 text-xs text-gray-500">
                <span className="font-semibold text-amber-600">min</span> = pemakaian di bawah Min. Usage kontrak;
                Total Usage memakai qty minimum yang disetujui &amp; ditagih (kolom harian tetap pemakaian riil).
              </p>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-2 py-2 font-semibold border border-gray-300 whitespace-nowrap ${className}`}>
      {children}
    </th>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
      <p className={`text-sm text-gray-800 ${mono ? "font-mono text-xs" : ""}`}>
        {value || <span className="text-gray-400">—</span>}
      </p>
    </div>
  );
}
